import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FileText, Plus, Trash2, Edit } from "lucide-react";
import {
  getUserTemplates,
  saveUserTemplate,
  deleteUserTemplate,
} from "../../api/const";
import BoardTable from "../../components/common/board/BoardTable";
import BoardPagination from "../../components/common/board/BoardPagination";
import PageHeader from "../../components/common/ui/PageHeader";

export default function UserTemplateList() {
  const [templates, setTemplates] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newContent, setNewContent] = useState("");

  // 페이지네이션
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // 상태
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { id } = useParams(); // 프로젝트 ID
  const project = id;
  const navigate = useNavigate();

  const fetchTemplates = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getUserTemplates(project, {
        page,
        page_size: pageSize,
      });
      console.log(res);
      if (Array.isArray(res)) {
        setTemplates(res);
        setTotal(res.length);
      } else {
        setTemplates(res.results);
        setTotal(res.count);
      }
    } catch (error) {
      console.error("템플릿 불러오기 실패:", error);
      setError("템플릿을 불러올 수 없습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [page, pageSize]);

  const handleCreate = async () => {
    try {
      const newTemplate = await saveUserTemplate({
        project,
        title: newTitle || "새 템플릿",
        template: newContent || "",
      });
      alert(`새 템플릿 생성됨: ${newTemplate.id}`);
      setIsModalOpen(false);
      setNewTitle("");
      setNewContent("");
      fetchTemplates();
    } catch (error) {
      console.error("생성 실패:", error);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;
    try {
      await deleteUserTemplate(templateId);
      alert("삭제 완료");
      fetchTemplates();
    } catch (error) {
      console.error("삭제 실패:", error);
    }
  };

  const handleRowClick = (template) => {
    navigate(`/project/${project}/const/progress/new`, {
      state: {
        templateId: template.id,
        templateContent: template.template,
        templateTitle: template.title,
        templateType: template.period_type,
      },
    });
  };

  const columns = [
    {
      key: "title",
      header: "제목",
      align: "left", // <td> 정렬
      headerAlign: "center", // <th> 정렬
    },
    {
      key: "period_type",
      header: "종류",
      render: (row) => (row.period_type === "monthly" ? "월간" : "주간"),
    },
    {
      key: "created_by",
      header: "작성자",
      render: (row) => row.created_by.name ?? "-",
    },
    {
      key: "created_date",
      header: "작성일",
      render: (row) => new Date(row.created_date).toLocaleDateString(),
    },
    {
      key: "updated_date",
      header: "수정일",
      render: (row) => new Date(row.updated_date).toLocaleDateString(),
    },
    {
      key: "actions",
      header: "관리",
      // width: "120px",
      render: (row) => (
        <div className="flex gap-2 justify-center">
          <button
            onClick={(e) => {
              e.stopPropagation(); // ✅ 클릭 전파 막기
              navigate(`/project/${project}/const/userTemplate/view/${row.id}`);
            }}
            className="p-1.5 rounded hover:bg-blue-50 text-blue-600"
            title="편집"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(row.id);
            }}
            className="p-1.5 rounded hover:bg-red-50 text-red-500"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      <PageHeader>사용자 템플릿</PageHeader>
      {/* 헤더 */}
      <div className="flex justify-end items-center mb-6">
        <div className="flex gap-2">
          <button
            // onClick={() => setIsModalOpen(true)}
            onClick={() =>
              navigate(`/project/${project}/const/userTemplate/new`)
            }
            className="btn-create"
          >
            새 템플릿 등록
          </button>
          {/* <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-700"
          >
            뒤로가기
          </button> */}
        </div>
      </div>

      {/* 리스트 */}
      <BoardTable
        columns={columns}
        rows={templates}
        loading={loading}
        error={error}
        emptyText="템플릿이 없습니다."
        onRowClick={(row) => handleRowClick(row)}
      />

      {/* 페이지네이션 */}
      <div className="mt-4">
        <BoardPagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
        />
      </div>
      {/* 모달 */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white w-96 p-6 rounded-lg shadow-lg">
            <h3 className="text-lg font-semibold mb-4">새 템플릿 작성</h3>
            <input
              type="text"
              placeholder="제목"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="w-full border rounded p-2 mb-3"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 bg-gray-300 rounded"
              >
                취소
              </button>
              <button
                onClick={handleCreate}
                className="px-4 py-2 bg-blue-500 text-white rounded"
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
